
import { defineComponent, ref } from "vue";
import {
  minLength,
  required,
  requiredIf,
  helpers,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import ToolTip from "@/components/common/ToolTip.vue";
import vueRecaptcha from "vue3-recaptcha2";
import validateSsn from "@/utils/validations/validateSsn";
import alphaNum from "@/utils/validations/alphaNum";
import { vMaska } from "maska";
import messages from "@/config/messages";
import common from "@/config/common";
import { mapMutations, mapGetters } from "vuex";
import routesPath from "@/config/routesPath";
import * as apiClient from "@/apiclient/";
import TokenService from "@/services/tokenService";
import PandALogin from "@/components/account/PandALogin.vue";
import SearchParticipants from "@/components/account/SearchParticipants.vue";
import store from "@/store";
// Custom wrapper to conditionally run a validator
const conditionalValidator = (conditionFn: () => boolean, validator: any) => {
  return helpers.withParams({ type: "conditional" }, (value: any) => {
    return !conditionFn() || validator.$validator(value);
  });
};

interface UserData {
  userName: string;
  password: string;
  registrationCode: string;
  ssn: string;
  gRecaptcha: string;
}

export default defineComponent({
  name: "GetStartedView",
  components: {
    ToolTip,
    vueRecaptcha,
    PandALogin,
    SearchParticipants,
  },
  directives: { maska: vMaska },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      user: {
        userName: "",
        password: "",
        registrationCode: "",
        ssn: "",
        gRecaptcha: "",
      } as UserData,

      submitted: false,
      showRecaptcha: false,
      loadingTimeout: 30000,
      isFormInvalid: false,
      validationMsg: "Invalid registration code or SSN. Please try again",
      isSubmitDisabled: false,
      messages,
      googleRecaptchaKey: store.state.settings.googleRecaptchaKey ?? "",
      activeIndex: 0,
      loginResponse: {} as apiClient.LoginResponse,
      routesPath,
    };
  },
  validations() {
    const isRegCode = this.activeIndex === 1;
    const isAccountLogin = this.activeIndex === 0;

    return {
      user: {
        registrationCode: {
          ...(isRegCode && {
            required,
            minLength: minLength(8),
            alphaNum,
          }),
        },
        ssn: {
          ...(isRegCode && {
            required,
            validateSsn: (value: string) => {
              return !value || validateSsn(value);
            },
          }),
        },
        gRecaptcha: {
          ...(isRegCode && {
            required,
          }),
        },
        userName: {
          ...(isAccountLogin && {
            required,
          }),
        },
        password: {
          ...(isAccountLogin && {
            required,
          }),
        },
      },
    };
  },
  computed: {
    ...mapGetters(["getAutoLogoutMsg"]),
  },
  methods: {
    ...mapMutations(["setAutoLogoutMsg"]),
    hideErrors(field: string): void {
      this.v$.user[field].$touch();
      this.isFormInvalid = false;
    },
    toggleAccordion(index: number) {
      if (this.activeIndex === index) return;
      this.user.userName = "";
      this.user.password = "";
      this.user.ssn = "";
      this.user.registrationCode = "";
      this.user.gRecaptcha = "";
      this.v$.$reset();
      this.activeIndex = index;
      if (index === 1) {
        this.showRecaptcha = true;
      } else {
        this.showRecaptcha = false;
      }
      TokenService.logout();
    },
    buttonDesign(): string {
      const buttonStatus = this.v$.user.$invalid;
      return buttonStatus == true ? "btn-secondary" : "btn-primary";
    },
    async userLoginPandA(): Promise<void> {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      TokenService.logout();
      this.setAutoLogoutMsg("");
      const params = {
        username: this.user.userName,
        password: this.user.password,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .loginController()
        .postApiLoginAuthorize(params);

      this.isSubmitDisabled = false;
      alert(JSON.stringify(result.data));

      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.valid) {
        if (this.loginResponse.requireMFA) {
          const token: string = result.data?.mfaToken || "";
          TokenService.setToken(common.storageVariables.mfaToken, token);
          this.$router.push({ name: routesPath.mfa });
          return;
        }
        if (result.data?.requireChallenge) {
          const token: string = result.data?.challengeToken || "";
          TokenService.setToken("ChallengeToken", token);
          sessionStorage.setItem(
            common.storageVariables.challengeQuestion,
            result.data!.challengeQuestion!
          );
          this.$router.push({ name: routesPath.challenge });
          return;
        }
        const token: string = result.data?.participantToken?.token || "";
        const refreshToken: string =
          result.data?.participantToken?.refreshToken || "";

        TokenService.setToken("ParticipantToken", token);
        TokenService.setAccessToken(token);
        TokenService.setRefreshToken(refreshToken);

        this.$router.push({ name: routesPath.profileInformation });
        return;
      } else {
        this.isFormInvalid = true;
        const message: string = result.data?.message || "";
        this.validationMsg = message + messages.errorMsg;
        return;
      }
    },
    async userDataSubmit(): Promise<void> {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.setAutoLogoutMsg("");
      const params = {
        registrationCode: this.user.registrationCode,
        ssn: this.user.ssn,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient.loginController().postApiLogin(params);

      this.isSubmitDisabled = false;
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.emailAddress) {
        const email = result.data.emailAddress;
        const loginData = {
          registrationCode: params.registrationCode,
          ssn: params.ssn,
          email: email,
          lastDateToEnroll: result.data.lastDateToEnroll,
          remainingDaysToEnroll: result.data.remainingDaysToEnroll,
        };
        sessionStorage.setItem(
          common.storageVariables.loginData,
          JSON.stringify(loginData)
        );
        this.isFormInvalid = false;
        sessionStorage.setItem(
          common.storageVariables.otpTimer,
          common.otpTimer
        );
        this.$router.push({
          name: routesPath.otpVerification,
        });
      } else {
        const loginData = {
          registrationCode: params.registrationCode,
          ssn: params.ssn,
        };
        sessionStorage.setItem(
          common.storageVariables.loginData,
          JSON.stringify(loginData)
        );
        this.$router.push({
          name: routesPath.editEmail,
          query: { action: "register" },
        });
      }
    },
    recaptchaVerified(response: string): void {
      this.isSubmitDisabled = false;
      if (response) {
        this.user.gRecaptcha = response;
      }
    },
    recaptchaExpired(): void {
      if (vueRecaptcha.value) {
        vueRecaptcha.value.reset();
      }
      this.user.gRecaptcha = "";
    },
    recaptchaFailed(): void {
      this.user.gRecaptcha = "";
    },
    recaptchaError(reason: any): void {
      this.user.gRecaptcha = "";
    },
  },
});
